/**
 * IntelSourcesList — the source registry as a list.
 * Demo page: `/tables/data-tables` (search, filter pills, scoped select, selection, row ⋯ menu, footer). Template:
 * ListPage, the same shape as `pages/tasks/TasksPage.tsx`.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { buttonStyles } from '../../../components/tailgrids/core/button.js';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, ListPlus, Plus, Radar, RotateCw, Zap } from 'lucide-react';
import {
  batchDeactivateIntelSourcesMutation,
  batchFetchIntelSourcesMutation,
  fetchIntelSourceMutation,
  listIntelSourcesOptions,
} from '../../../generated/api/@tanstack/react-query.gen.js';
import type { IntelSource } from '../../../types/intel-source.js';
import { Can } from '../../../components/auth/Can.js';
import { ListPage } from '../../../components/page/ListPage.js';
import { Checkbox } from '../../../components/tailgrids/core/checkbox.js';
import {
  ActionMenu,
  Badge,
  Button,
  Callout,
  DataTable,
  EmptyState,
  FilterPills,
  SearchBar,
  Toolbar,
  useToast,
} from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';
import { usePolicy } from '../../../hooks/usePolicy.js';
import { PAGE_HELP } from '../../../help';
import { BulkImportModal } from './BulkImportModal.js';
import { DiscoverModal } from './DiscoverModal.js';
import { BulkFooter } from './BulkFooter.js';
import { CandidateSourcesPanel } from './CandidateSourcesPanel.js';
import { ActiveToggleCell } from './ActiveToggleCell.js';
import { FilterSelect } from '../../../components/ui/FilterSelect.js';
import { CATEGORY_OPTIONS } from './IntelFilterSelects.js';
import { SourceQualityRing } from './SourceQualityRing.js';
import { SourceRowCell } from './SourceRow.js';
import { formatRelativeTime } from './SourceCard.js';
import { sourcesListQuery, useIntelSourceInvalidation } from './intel-source-queries.js';
import { isStale, staleDays } from './source-freshness.js';

interface IntelSourcesListProps {
  /** Opens a source's detail view. */
  onOpen: (sourceId: number) => void;
}

type PillId = 'all' | 'active' | 'twitter' | 'telegram' | 'discord' | 'discovered' | 'stale';

const isPlatform = (s: IntelSource, ...names: string[]) => names.includes(s.platform.toLowerCase());

const PILL_FILTERS: Record<PillId, (s: IntelSource) => boolean> = {
  all: () => true,
  active: (s) => s.active,
  twitter: (s) => isPlatform(s, 'twitter', 'x'),
  telegram: (s) => isPlatform(s, 'telegram'),
  discord: (s) => isPlatform(s, 'discord'),
  discovered: (s) => !s.seed && s.discoveredAt != null,
  stale: isStale,
};

const PILL_LABELS: Record<PillId, string> = {
  all: 'All',
  active: 'Active',
  twitter: 'X',
  telegram: 'Telegram',
  discord: 'Discord',
  discovered: 'Discovered',
  stale: 'Stale',
};

export const IntelSourcesList: React.FC<IntelSourcesListProps> = ({ onOpen }) => {
  const canManage = usePolicy('CanManageAgents');
  const invalidate = useIntelSourceInvalidation();
  const { notify } = useToast();

  const { data, isLoading, isError, refetch } = useQuery(listIntelSourcesOptions(sourcesListQuery()));
  const sources: IntelSource[] = useMemo(() => data?.data ?? [], [data]);

  const [searchText, setSearchText] = useState('');
  const [pill, setPill] = useState<PillId>('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);

  const batchFetchMut = useMutation({
    ...batchFetchIntelSourcesMutation(),
    onSuccess: () => {
      invalidate();
      setSelectedIds(new Set());
      setFetchFailed(false);
    },
    onError: () => setFetchFailed(true),
  });
  const batchDeactivateMut = useMutation({
    ...batchDeactivateIntelSourcesMutation(),
    onSuccess: () => {
      invalidate();
      setSelectedIds(new Set());
    },
    onError: () => notify('Could not deactivate the selected sources.', 'danger'),
  });
  const fetchOneMut = useMutation({
    ...fetchIntelSourceMutation(),
    onSuccess: () => notify('Fetch queued.'),
    onError: () => notify('Could not queue a fetch.', 'danger'),
  });

  const fetchSelected = () => batchFetchMut.mutate({ body: { ids: Array.from(selectedIds) } });

  const pillOptions = (Object.keys(PILL_FILTERS) as PillId[]).map((id) => ({
    id,
    label: PILL_LABELS[id],
    count: sources.filter(PILL_FILTERS[id]).length,
  }));

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return sources
      .filter(PILL_FILTERS[pill])
      .filter(
        (s) =>
          (!categoryFilter || s.category === categoryFilter) &&
          (!q ||
            s.handle.toLowerCase().includes(q) ||
            (s.name ?? '').toLowerCase().includes(q) ||
            s.platform.toLowerCase().includes(q)),
      );
  }, [sources, pill, categoryFilter, searchText]);

  const toggleSelect = (id: number) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allSelected = filtered.length > 0 && selectedIds.size === filtered.length;
  const toggleSelectAll = () => setSelectedIds(allSelected ? new Set() : new Set(filtered.map((s) => s.id)));

  const columns: DataTableColumn<IntelSource>[] = [
    ...(canManage
      ? [
          {
            key: 'select',
            header: <Checkbox aria-label="Select all sources" isSelected={allSelected} onChange={toggleSelectAll} />,
            width: '44px',
            render: (row: IntelSource) => (
              // The row opens the detail on click; the checkbox must not.
              <span role="presentation" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                <Checkbox
                  aria-label={`Select ${row.name ?? row.handle}`}
                  isSelected={selectedIds.has(row.id)}
                  onChange={() => toggleSelect(row.id)}
                />
              </span>
            ),
          } satisfies DataTableColumn<IntelSource>,
        ]
      : []),
    {
      key: 'source',
      header: 'Source',
      width: '30%',
      sortable: true,
      sortValue: (row) => (row.name ?? row.handle).toLowerCase(),
      render: (row) => (
        <Link
          to={`?source=${row.id}`}
          className="block hover:opacity-80"
          onClick={(e) => e.stopPropagation()}
          aria-label={`Open ${row.name ?? row.handle}`}
        >
          <SourceRowCell source={row} />
        </Link>
      ),
    },
    {
      key: 'platform',
      header: 'Platform',
      sortable: true,
      sortValue: (row) => row.platform,
      render: (row) => <span className="capitalize text-muted">{row.platform}</span>,
    },
    {
      key: 'category',
      header: 'Category',
      sortable: true,
      sortValue: (row) => row.category,
      render: (row) => (
        <Badge variant="neutral" size="sm">
          {row.category.replace(/_/g, ' ')}
        </Badge>
      ),
    },
    {
      key: 'quality',
      header: 'Quality',
      sortable: true,
      sortValue: (row) => (row.overallScore == null ? null : parseFloat(row.overallScore)),
      render: (row) => <SourceQualityRing overallScore={row.overallScore} />,
    },
    {
      key: 'lastContent',
      header: 'Last content',
      sortable: true,
      sortValue: (row) => (row.lastContentAt ? new Date(row.lastContentAt).getTime() : null),
      render: (row) => {
        if (!row.lastContentAt) return <span className="text-faint">—</span>;
        if (isStale(row)) return <span className="text-warning">Stale, {staleDays(row)}d</span>;
        return <span className="text-muted">{formatRelativeTime(row.lastContentAt)}</span>;
      },
    },
    {
      key: 'active',
      header: 'Active',
      render: (row) => <ActiveToggleCell source={row} canManage={canManage} />,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '56px',
      render: (row) => (
        // The row opens the detail on click; the menu must not.
        <span role="presentation" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <ActionMenu
            aria-label={`Actions for ${row.name ?? row.handle}`}
            items={[
              { id: 'open', label: 'Open', onAction: () => onOpen(row.id) },
              ...(canManage
                ? [
                    {
                      id: 'fetch',
                      label: 'Fetch now',
                      icon: <Zap size={14} aria-hidden="true" />,
                      onAction: () => fetchOneMut.mutate({ path: { id: row.id }, body: {} }),
                    },
                  ]
                : []),
            ]}
          />
        </span>
      ),
    },
  ];

  const hasFilters = pill !== 'all' || categoryFilter !== '' || searchText.trim() !== '';
  const help = PAGE_HELP['intel-sources'];

  const addButton = (
    <Can policy="CanManageAgents">
      <Link to="/agents/intel-sources/new" className={buttonStyles({ variant: 'primary', size: 'sm' })}>
        <Plus size={14} aria-hidden="true" /> Add source
      </Link>
    </Can>
  );
  const discoverButton = (
    <Can policy="CanManageAgents">
      <Button variant="secondary" size="sm" onClick={() => setDiscoverOpen(true)}>
        <Radar size={14} aria-hidden="true" /> Discover
      </Button>
    </Can>
  );

  return (
    <>
      <ListPage
        title="Intel sources"
        breadcrumbs={[{ label: 'Knowledge' }, { label: 'Intel sources' }]}
        subtitle="The sources your agents watch for signals: YouTube, X, RSS, Discord and on-chain. Open a source to read what it collected."
        helpContent={help}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {discoverButton}
            <Can policy="CanManageAgents">
              <Button variant="secondary" size="sm" onClick={() => setImportOpen(true)}>
                <ListPlus size={14} aria-hidden="true" /> Import list
              </Button>
            </Can>
            {addButton}
          </div>
        }
        alert={
          <>
            {isError && (
              <Callout
                tone="danger"
                icon={AlertTriangle}
                title="Could not load intel sources"
                actions={
                  <Button size="sm" variant="secondary" onClick={() => refetch()}>
                    Retry
                  </Button>
                }
              >
                The source registry failed to load. Check your connection and try again.
              </Callout>
            )}
            {fetchFailed && (
              <Callout
                tone="danger"
                icon={AlertTriangle}
                title="Fetching the selected sources failed"
                actions={
                  <>
                    <Button size="sm" variant="secondary" onClick={fetchSelected}>
                      <RotateCw size={12} aria-hidden="true" /> Retry
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setFetchFailed(false)}>
                      Dismiss
                    </Button>
                  </>
                }
              >
                The platform may have rate-limited the request. Sources that were not fetched are retried on their next
                scheduled run.
              </Callout>
            )}
            <CandidateSourcesPanel />
          </>
        }
        toolbar={
          <Toolbar
            search={<SearchBar value={searchText} onChange={setSearchText} placeholder="Search handle or name…" />}
            pills={
              <FilterPills
                aria-label="Source filter"
                options={pillOptions}
                value={pill}
                onChange={(id) => setPill(id as PillId)}
              />
            }
            right={
              <>
                <FilterSelect
                  label="Category"
                  anyLabel="Any category"
                  className="w-48"
                  value={categoryFilter}
                  options={CATEGORY_OPTIONS}
                  onChange={setCategoryFilter}
                />
                <Can policy="CanManageAgents">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={selectedIds.size === 0}
                    loading={batchFetchMut.isPending}
                    onClick={fetchSelected}
                  >
                    <Zap size={14} aria-hidden="true" /> Fetch selected
                  </Button>
                </Can>
              </>
            }
          />
        }
      >
        {!isError && (
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(row) => row.id}
            getRowAttention={(row) => (isStale(row) ? 'warning' : undefined)}
            onRowClick={(row) => onOpen(row.id)}
            loading={isLoading}
            aria-label="Intel sources"
            footer={
              filtered.length > 0 ? (
                <BulkFooter
                  total={sources.length}
                  shown={filtered.length}
                  selectedCount={selectedIds.size}
                  sources={sources}
                  fetchPending={batchFetchMut.isPending}
                  deactivatePending={batchDeactivateMut.isPending}
                  onBatchFetch={fetchSelected}
                  onBatchDeactivate={() => batchDeactivateMut.mutate({ body: { ids: Array.from(selectedIds) } })}
                />
              ) : undefined
            }
            empty={
              sources.length === 0 ? (
                <EmptyState
                  icon={Radar}
                  title={help?.emptyState?.title ?? 'No intel sources yet'}
                  description="Add handles by hand, import a list, or run discovery to find high-signal accounts to watch."
                  learnItems={help?.emptyState?.learnItems}
                  action={
                    <div className="flex justify-center gap-2">
                      {addButton}
                      {discoverButton}
                    </div>
                  }
                />
              ) : (
                <EmptyState
                  icon={Radar}
                  title="No sources match"
                  description={hasFilters ? 'Nothing matches the current search and filters.' : 'Nothing to show.'}
                  action={
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setPill('all');
                        setCategoryFilter('');
                        setSearchText('');
                      }}
                    >
                      Clear filters
                    </Button>
                  }
                />
              )
            }
          />
        )}
      </ListPage>

      <Can policy="CanManageAgents">
        <BulkImportModal open={importOpen} onClose={() => setImportOpen(false)} onImported={() => invalidate()} />
        {/* Stays open after queuing: the modal confirms "Discovery queued" and closes with Done. */}
        <DiscoverModal open={discoverOpen} onClose={() => setDiscoverOpen(false)} onCreated={() => invalidate()} />
      </Can>
    </>
  );
};
